import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { MonthView, WeekView, DayView, sameDay, WEEKDAYS } from "../components/calendar/CalendarViews";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ChevronLeft, ChevronRight, CalendarDays, X, FileDown, CalendarPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const LEGEND = [
  { key: "pubblicata", label: "Pubblicata", dot: "bg-amber-500" },
  { key: "assegnato", label: "Assegnato", dot: "bg-blue-600" },
  { key: "completata", label: "Completata", dot: "bg-emerald-600" },
  { key: "da_fatturare", label: "Fatturare", dot: "bg-red-600" },
  { key: "annullata", label: "Annullata", dot: "bg-red-600" },
];

function parseEvent(value) {
  if (!value) return null;
  const d = new Date(String(value).trim().replace(" ", "T"));
  return isNaN(d.getTime()) ? null : d;
}

const startOfWeek = (date) => {
  const d = new Date(date);
  const diff = (d.getDay() + 6) % 7; // lun=0
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

export default function CalendarPage() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [partners, setPartners] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [partnerFilter, setPartnerFilter] = useState("all");
  const [view, setView] = useState(() => localStorage.getItem("nx_cal_view") || "month");
  const [anchor, setAnchor] = useState(new Date());
  const today = new Date();

  useEffect(() => {
    api.get("/requests").then(({ data }) => setRequests(data)).catch(() => setRequests([]));
    api.get("/partners").then(({ data }) => setPartners(data)).catch(() => setPartners([]));
  }, []);

  useEffect(() => { localStorage.setItem("nx_cal_view", view); }, [view]);

  // All events matching filters with a valid date
  const events = useMemo(() => {
    const out = [];
    for (const r of requests) {
      if (statusFilter !== "all" && r.status !== statusFilter) continue;
      if (partnerFilter !== "all" && r.assigned_partner_id !== partnerFilter) continue;
      const d = parseEvent(r.operative?.data_ora_evento);
      if (!d) continue;
      out.push({ ...r, _time: d });
    }
    out.sort((a, b) => a._time - b._time);
    return out;
  }, [requests, statusFilter, partnerFilter]);

  const eventsForDay = (date) => events.filter((e) => sameDay(e._time, date));

  const hasFilters = statusFilter !== "all" || partnerFilter !== "all";

  // Period bounds & count for current view
  const weekStart = useMemo(() => startOfWeek(anchor), [anchor]);
  const weekDays = useMemo(() => [...Array(7)].map((_, i) => {
    const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d;
  }), [weekStart]);

  const totalShown = useMemo(() => {
    if (view === "day") return eventsForDay(anchor).length;
    if (view === "week") {
      const end = new Date(weekStart); end.setDate(weekStart.getDate() + 7);
      return events.filter((e) => e._time >= weekStart && e._time < end).length;
    }
    return events.filter((e) => e._time.getFullYear() === anchor.getFullYear() && e._time.getMonth() === anchor.getMonth()).length;
  }, [view, anchor, events, weekStart]); // eslint-disable-line

  const move = (delta) => {
    setAnchor((a) => {
      const d = new Date(a);
      if (view === "month") d.setMonth(d.getMonth() + delta);
      else if (view === "week") d.setDate(d.getDate() + delta * 7);
      else d.setDate(d.getDate() + delta);
      return d;
    });
  };
  const goToday = () => setAnchor(new Date());

  const label = useMemo(() => {
    if (view === "month") return `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`;
    if (view === "week") {
      const end = weekDays[6];
      const sameMonth = weekStart.getMonth() === end.getMonth();
      const l = `${weekStart.getDate()} ${MONTHS[weekStart.getMonth()].slice(0, 3)}`;
      const r = `${end.getDate()} ${MONTHS[end.getMonth()].slice(0, 3)} ${end.getFullYear()}`;
      return sameMonth ? `${weekStart.getDate()}–${end.getDate()} ${MONTHS[end.getMonth()]} ${end.getFullYear()}` : `${l} – ${r}`;
    }
    return `${WEEKDAYS[(anchor.getDay() + 6) % 7]} ${anchor.getDate()} ${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`;
  }, [view, anchor, weekStart, weekDays]);

  const [exporting, setExporting] = useState("");
  const exportCalendar = async (format) => {
    setExporting(format);
    try {
      const params = { year: anchor.getFullYear(), month: anchor.getMonth() + 1, format };
      if (statusFilter !== "all") params.status = statusFilter;
      if (partnerFilter !== "all") params.partner_id = partnerFilter;
      const res = await api.get("/reports/calendar", { params, responseType: "blob" });
      const type = format === "ics" ? "text/calendar" : "application/pdf";
      const url = window.URL.createObjectURL(new Blob([res.data], { type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Calendario_Nexus_${anchor.getFullYear()}${String(anchor.getMonth() + 1).padStart(2, "0")}.${format === "ics" ? "ics" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Esportazione non riuscita");
    } finally { setExporting(""); }
  };

  const openEvent = (ev) => navigate(`/operativa/richiesta/${ev.id}`);

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto nx-fade-in">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2">
              <CalendarDays className="h-7 w-7 text-primary" /> Calendario Corse
            </h1>
            <p className="text-sm text-slate-500 mt-1">Prenotazioni colorate per stato · vista mese, settimana e giorno</p>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="cal-prev" variant="outline" size="icon" onClick={() => move(-1)}><ChevronLeft className="h-4 w-4" /></Button>
            <div data-testid="cal-label" className="min-w-[210px] text-center font-semibold text-slate-800 font-heading">
              {label}
            </div>
            <Button data-testid="cal-next" variant="outline" size="icon" onClick={() => move(1)}><ChevronRight className="h-4 w-4" /></Button>
            <Button data-testid="cal-today" variant="ghost" onClick={goToday}>Oggi</Button>
          </div>
        </div>

        {/* View switcher */}
        <div className="inline-flex rounded-lg border border-slate-200 p-1 mb-4 bg-white">
          {[["month", "Mese"], ["week", "Settimana"], ["day", "Giorno"]].map(([key, lbl]) => (
            <button key={key} data-testid={`cal-view-${key}`} onClick={() => setView(key)}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${view === key ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              {lbl}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="cal-filter-status" className="h-9 w-44"><SelectValue placeholder="Stato" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="pubblicata">Pubblicata</SelectItem>
              <SelectItem value="assegnato">Assegnato</SelectItem>
              <SelectItem value="completata">Completata</SelectItem>
              <SelectItem value="da_fatturare">Fatturare</SelectItem>
              <SelectItem value="annullata">Annullata</SelectItem>
            </SelectContent>
          </Select>
          <Select value={partnerFilter} onValueChange={setPartnerFilter}>
            <SelectTrigger data-testid="cal-filter-partner" className="h-9 w-56"><SelectValue placeholder="Partner" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i partner</SelectItem>
              {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {hasFilters && (
            <Button data-testid="cal-clear-filters" variant="ghost" size="sm" className="gap-1 h-9"
              onClick={() => { setStatusFilter("all"); setPartnerFilter("all"); }}>
              <X className="h-4 w-4" /> Reset
            </Button>
          )}
          <span className="text-xs text-slate-400 ml-auto" data-testid="cal-count">{totalShown} corse</span>
          <Button data-testid="cal-export-pdf" variant="outline" size="sm" className="gap-1.5 h-9" onClick={() => exportCalendar("pdf")} disabled={!!exporting}>
            {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} PDF
          </Button>
          <Button data-testid="cal-export-ics" variant="outline" size="sm" className="gap-1.5 h-9" onClick={() => exportCalendar("ics")} disabled={!!exporting}>
            {exporting === "ics" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarPlus className="h-4 w-4" />} iCal
          </Button>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-4">
          {LEGEND.map((l) => (
            <div key={l.key} className="flex items-center gap-1.5 text-xs text-slate-600">
              <span className={`h-2.5 w-2.5 rounded-full ${l.dot}`} /> {l.label}
            </div>
          ))}
        </div>

        {view === "month" && <MonthView anchor={anchor} today={today} eventsForDay={eventsForDay} onOpen={openEvent} />}
        {view === "week" && <WeekView weekDays={weekDays} today={today} eventsForDay={eventsForDay} onOpen={openEvent} />}
        {view === "day" && <DayView anchor={anchor} eventsForDay={eventsForDay} onOpen={openEvent} />}
      </div>
    </AppLayout>
  );
}
